// A failed sign-in round trip, in words a dealer can act on.
//
// Everything that can end a round trip arrives as a machine code — an OAuth
// `error` the provider put in the query, an identity-server body error, or one
// this lib minted (`LOGIN_STATE_MISMATCH`, `TOKEN_EXCHANGE_FAILED_500`). None of
// them is copy. The retry screen used to paint whatever it was handed, so a
// 500 at the identity server showed a dealer `INTERNAL_ERROR` under the Dynamic
// Island; an unreadable value showed a JSON blob. So: nothing reaches the
// screen but a key from the table below, and a code nobody here recognizes is
// still answered with a lede rather than shown.
//
// Pure and surface-agnostic, like identityRoutes.js, because the mobile and the
// desktop retry screens must say the same thing and a test must be able to
// state that a raw code never does.

const MESSAGE_KEYS = Object.freeze({
  accountNotLinked: 'authentication.login.accountNotLinked',
  cancelled: 'authentication.login.failedCancelled',
  expired: 'authentication.login.failedExpired',
  service: 'authentication.login.failedService',
  connection: 'authentication.login.noConnectionMessage',
  generic: 'authentication.login.failedGeneric'
});

const KEY_BY_CODE = Object.freeze({
  ACCOUNT_NOT_LINKED: MESSAGE_KEYS.accountNotLinked,
  ACCESS_DENIED: MESSAGE_KEYS.cancelled,
  LOGIN_REQUIRED: MESSAGE_KEYS.cancelled,
  INVALID_REQUEST: MESSAGE_KEYS.service,
  SERVER_ERROR: MESSAGE_KEYS.service,
  TEMPORARILY_UNAVAILABLE: MESSAGE_KEYS.service,
  INTERNAL_ERROR: MESSAGE_KEYS.service,
  LOGIN_STATE_MISMATCH: MESSAGE_KEYS.expired
});

// A code and nothing else. A value with a brace, a quote or a space in it is
// somebody's serialized error object, and the only safe thing to do with one is
// to not recognize it.
const CODE_SHAPE = /^[A-Za-z0-9_.:-]{1,64}$/;

function reportedValue(error) {
  if (Array.isArray(error)) {
    return error[0];
  }
  return error;
}

function codeOf(reported) {
  if (typeof reported !== 'string') {
    return '';
  }
  const trimmed = reported.trim();
  if (!CODE_SHAPE.test(trimmed)) {
    return '';
  }
  return trimmed.toUpperCase();
}

// The exchange failure carries its own cause: `TOKEN_EXCHANGE_FAILED_NETWORK`
// is the phone's signal, any status is the service.
function keyForFamily(code) {
  if (code.includes('NETWORK')) {
    return MESSAGE_KEYS.connection;
  }
  if (code.startsWith('TOKEN_EXCHANGE_FAILED_')) {
    return MESSAGE_KEYS.service;
  }
  return '';
}

/**
 * The i18n key the retry screen shows for a round trip that failed, or `''`
 * when nothing failed. Always a key: an unrecognized or unreadable value
 * resolves to the generic lede rather than being passed through.
 */
export function loginFailureMessageKey(error) {
  const reported = reportedValue(error);
  if (!reported) {
    return '';
  }
  const code = codeOf(reported);
  return KEY_BY_CODE[code] || keyForFamily(code) || MESSAGE_KEYS.generic;
}

export { MESSAGE_KEYS as LOGIN_FAILURE_MESSAGE_KEYS };
